'use client'

import { useState, type ReactNode } from 'react'
import { usePathname } from 'next/navigation'
import { Modal } from '../../ui/Modal'
import { ActionSheet } from '../../ui/ActionSheet/ActionSheet'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { useTranslation } from '../../../hooks/useTranslation'
import { ActivityInlineDescription } from '../shared/ActivityDescription'
import { publicFontClassName } from '../../../lib/public-font'

/** An offer preview: a bottom sheet on phones, a dialog elsewhere. The footer carries the booking action. */
export function PublicOfferDetailsTrigger({
    name,
    subtitle,
    triggerLabel,
    description,
    fullDescriptionHtml,
    className,
    summary,
    footer,
    children,
}: {
    name: string
    subtitle?: string
    triggerLabel?: string
    description?: string | null
    fullDescriptionHtml?: string | null
    className?: string
    summary?: ReactNode
    /** A function receives `close`, for an action that leaves this window for another one. */
    footer?: ReactNode | ((close: () => void) => ReactNode)
    children?: ReactNode
}) {
    // Open only on the path it was opened from: a footer link that navigates (an intercepted event page) closes it.
    const pathname = usePathname()
    const [openedOn, setOpenedOn] = useState<string | null>(null)
    const open = openedOn === pathname
    const { isMobile } = useIsMobile()
    const { t } = useTranslation('common')
    const close = () => setOpenedOn(null)
    const footerNode = typeof footer === 'function' ? footer(close) : footer
    const content = (
        <div className="flex flex-col gap-4 text-left">
            {open ? <ActivityInlineDescription description={description} fullDescriptionHtml={fullDescriptionHtml}/> : null}
            {summary}
            {open && children ? <div className="empty:hidden">{children}</div> : null}
        </div>
    )
    return (
        <>
            <button type="button" className={className} onClick={() => setOpenedOn(pathname)} aria-haspopup="dialog">
                {triggerLabel ?? name}
            </button>
            {isMobile ? (
                <ActionSheet
                    open={open}
                    onClose={close}
                    title={name}
                    description={subtitle}
                    closeLabel={t('close')}
                    footer={footerNode}
                    showCancel={false}
                    className={publicFontClassName}
                    contentClassName="pb-4"
                >
                    {content}
                </ActionSheet>
            ) : (
                <Modal
                    isOpen={open}
                    onClose={close}
                    title={name}
                    description={subtitle}
                    size="medium"
                    className={publicFontClassName}
                    footer={footerNode}
                >
                    {content}
                </Modal>
            )}
        </>
    )
}
